/**
 * A team's stance on a bill, in one vocabulary (docs/PERMISSIONS.md §13). Shared by the Worker and the client.
 *
 * Stored (ACCOUNTS dashboard_team_positions.stance, the Alignment list, /api/v2): "supports" | "opposes".
 * Shown on the Legislation page and used by the forecast perspective (`Position` in forecast.ts): "support" | "oppose".
 * The old KV value (`team-position:<org>:<matter>`) used the second form. Everything that reads or writes a stance goes
 * through toStance() / toPosition(), so the two can't drift apart again.
 */
export type Stance = "supports" | "opposes";
export type StancePosition = "support" | "oppose";
export const STANCES: readonly Stance[] = ["supports", "opposes"];

/** Any spelling we accept ("support", "Supports", " oppose ", …) → the stored form; anything else → null. */
export function toStance(v: unknown): Stance | null {
  const s = typeof v === "string" ? v.trim().toLowerCase() : "";
  if (s === "support" || s === "supports") return "supports";
  if (s === "oppose" || s === "opposes") return "opposes";
  return null;
}

/** The stored form (or any accepted spelling) → the Legislation page's "support" | "oppose"; anything else → null. */
export function toPosition(v: unknown): StancePosition | null {
  const s = toStance(v);
  return s === "supports" ? "support" : s === "opposes" ? "oppose" : null;
}

/** Record numbers are stored upper-case and trimmed ("o2026-1234 " → "O2026-1234"); empty → null. */
export function recordKey(v: unknown): string | null {
  const s = typeof v === "string" ? v.trim().toUpperCase().slice(0, 40) : "";
  return s || null;
}
